import React, { Fragment, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import KeyAuditViewListItem from "../../../components/lists/listItems/KeyAuditViewListItem/KeyAuditViewListItem";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";

import { api, IKeyManagerGetAuditItems, KeyAuditItem, KeyAuditItemStatus } from "../../../logic/api";

import { SessionContext } from "../../../utility/useSession";
import { useDate } from "../../../utility/useDate";

import DashPage from "../../dash/DashPage";
import { useTranslation } from "react-i18next";

interface IKeyAuditViewParams {
    auditId: string;
}

const KeyAuditView = () => {
    const session = useContext(SessionContext);
    const params = useParams<IKeyAuditViewParams>();
    const { toFormattedDateTimeString } = useDate();
    const auditId = parseInt(params.auditId);
    const { t } = useTranslation();

    const [paramsScanned, setParamsScanned] = useState<IKeyManagerGetAuditItems | null>(null);
    const [paramsMissing, setParamsMissing] = useState<IKeyManagerGetAuditItems | null>(null);
    const [paramsUnexpected, setParamsUnexpected] = useState<IKeyManagerGetAuditItems | null>(null);

    const fetchAudit = api.hooks.keyManager.getAudit();
    useEffect(() => {
        if (!isNaN(auditId)) {
            fetchAudit.send(session.current.context, {
                auditId: auditId
            });
        }
    }, [auditId]);

    useEffect(() => {
        if (fetchAudit.data != null && fetchAudit.data.id != null) {
            setParamsScanned({
                auditId: fetchAudit.data.id,
                status: KeyAuditItemStatus.expected
            });
            setParamsMissing({
                auditId: fetchAudit.data.id,
                status: KeyAuditItemStatus.missing
            });
            setParamsUnexpected({
                auditId: fetchAudit.data.id,
                status: KeyAuditItemStatus.unexpected
            });
        } else {
            setParamsScanned(null);
        }
    }, [fetchAudit.data]);

    return <DashPage>
        <ProgressView status={fetchAudit.status}>

            {fetchAudit.data != null && (
                <Fragment>
                    <div style={{ padding: 20, textAlign: "center" }}>
                        <div>
                            <h1>{fetchAudit.data.location.parentName}, {fetchAudit.data.location.name}</h1>
                            <h2>{toFormattedDateTimeString(new Date(fetchAudit.data.createDate))} - {fetchAudit.data.createdBy.firstName} {fetchAudit.data.createdBy.lastName}</h2>
                        </div>

                        <TabbedView>
                            <TabbedViewPage title={t("keys.missingCount", { count: fetchAudit.data.missingCount })}>
                                <div style={{ marginBottom: 20 }}>
                                    <i>
                                        {t("keys.keysNotScanned", { location: fetchAudit.data.location.name })}
                                    </i>
                                </div>
                                {paramsMissing != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IKeyManagerGetAuditItems, KeyAuditItem>
                                            apiHook={api.hooks.keyManager.getAuditItems}
                                            parameters={paramsMissing}
                                            renderItem={(item) => {
                                                return <KeyAuditViewListItem item={item}></KeyAuditViewListItem>
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("input.validations.noMissingKeys")}</b>
                                            </div>
                                        </PagedDataView>
                                    </div>
                                )}
                            </TabbedViewPage>

                            <TabbedViewPage title={t("keys.unexpectedCount", { count: fetchAudit.data.unexpectedCount })}>
                                <div style={{ marginBottom: 20 }}><i>
                                    {t("keys.keysNotExpected", { location: fetchAudit.data.location.name })}                                    </i></div>
                                {paramsUnexpected != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IKeyManagerGetAuditItems, KeyAuditItem>
                                            apiHook={api.hooks.keyManager.getAuditItems}
                                            parameters={paramsUnexpected}
                                            renderItem={(item) => {
                                                return <KeyAuditViewListItem item={item}></KeyAuditViewListItem>
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("input.validations.noUnexpectedKeys")}</b>
                                            </div>
                                        </PagedDataView>
                                    </div>
                                )}
                            </TabbedViewPage>
                            <TabbedViewPage title={`Found (${fetchAudit.data.scanCount - fetchAudit.data.unexpectedCount})`}>
                                <div style={{ marginBottom: 20 }}><i>
                                    {t("keys.keysFoundDuringAudit")}
                                </i></div>
                                {paramsScanned != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IKeyManagerGetAuditItems, KeyAuditItem>
                                            apiHook={api.hooks.keyManager.getAuditItems}
                                            parameters={paramsScanned}
                                            renderItem={(item) => {
                                                return <KeyAuditViewListItem item={item}></KeyAuditViewListItem>
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("input.validations.noExpectedKeys")}</b>
                                            </div>
                                        </PagedDataView>
                                    </div>
                                )}
                            </TabbedViewPage>
                        </TabbedView>

                    </div>
                </Fragment>
            )}
        </ProgressView>
    </DashPage>
}

export default KeyAuditView;